/**
 * Newest-first cursor feed for the portal Home cards (`/portal/activity`,
 * `/portal/mail`), kept fresh while the member watches.
 *
 * The first load and "Show more" follow the server's opaque `nextCursor`. A
 * background refresh (window focus, the tab becoming visible, and every
 * `REFRESH_INTERVAL_MS` while visible) refetches page one and prepends only
 * the rows not already shown, keyed by `keyOf`. It keeps the existing
 * cursor: that cursor still points past the oldest loaded row, so "Show more"
 * never repeats or skips a row after a refresh. A refresh shows no spinner,
 * keeps the state object when nothing is new (no re-render flicker), ignores
 * transient failures, and is dropped while another request for the feed is in
 * flight, so focus and visibility firing together cost one request.
 *
 * A 401 on any request calls `onSignOut`; a 503 marks the feed `inert` (mail
 * not configured for the environment).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, apiFetch } from "@dearbill/shared";
import { useLiveRefresh } from "./useLiveRefresh";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Background refresh cadence while the page is visible. */
export const REFRESH_INTERVAL_MS = 60_000;

interface FeedPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface PortalFeed<T> {
  /** Loaded rows, newest first; null until the first page lands. */
  items: T[] | null;
  /** True when a further page exists. */
  hasMore: boolean;
  loadingMore: boolean;
  /** The last explicit load (first page, retry or Show more) failed. */
  error: boolean;
  /** The endpoint answered 503: the surface is not configured. */
  inert: boolean;
  /** Fetch the next page and append it. */
  loadMore: () => void;
  /** Restart from page one (the retry after a failed first load). */
  retry: () => void;
}

interface FeedState<T> {
  items: T[] | null;
  cursor: string | null;
}

/** `fresh` followed by the `prev` rows it doesn't already contain; `prev` itself when nothing is new. */
function mergeHead<T>(prev: T[], fresh: T[], keyOf: (item: T) => string): T[] {
  const known = new Set(prev.map(keyOf));
  if (fresh.every((item) => known.has(keyOf(item)))) return prev;
  const freshKeys = new Set(fresh.map(keyOf));
  return [...fresh, ...prev.filter((item) => !freshKeys.has(keyOf(item)))];
}

export function usePortalFeed<T>(
  path: string,
  session: string,
  onSignOut: () => void,
  keyOf: (item: T) => string,
): PortalFeed<T> {
  const [state, setState] = useState<FeedState<T>>({ items: null, cursor: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [inert, setInert] = useState(false);
  // Requests in flight for this feed; a background refresh waits its turn.
  const pending = useRef(0);
  const alive = useRef(true);
  const keyRef = useRef(keyOf);
  useEffect(() => {
    keyRef.current = keyOf;
  }, [keyOf]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const fetchPage = useCallback(
    (after: string | null): Promise<FeedPage<T>> => {
      pending.current += 1;
      return apiFetch<FeedPage<T>>(
        after === null ? path : `${path}?cursor=${encodeURIComponent(after)}`,
        {},
        { baseUrl: API_BASE, token: session },
      ).finally(() => {
        pending.current -= 1;
      });
    },
    [path, session],
  );

  const handleError = useCallback(
    (err: unknown, surface: boolean): void => {
      if (err instanceof ApiError && err.status === 401) {
        onSignOut();
        return;
      }
      if (err instanceof ApiError && err.status === 503) {
        setInert(true);
        return;
      }
      if (surface) setError(true);
    },
    [onSignOut],
  );

  /** An explicit load: page one (`after` null) replaces the list, a cursor appends. */
  const load = useCallback(
    (after: string | null): void => {
      setError(false);
      if (after !== null) setLoadingMore(true);
      fetchPage(after)
        .then((page) => {
          if (!alive.current) return;
          setState((prev) => {
            if (after === null || prev.items === null) {
              return { items: page.items, cursor: page.nextCursor };
            }
            // A refresh that landed meanwhile may already hold some of these rows.
            const known = new Set(prev.items.map(keyRef.current));
            const older = page.items.filter((item) => !known.has(keyRef.current(item)));
            return { items: [...prev.items, ...older], cursor: page.nextCursor };
          });
        })
        .catch((err: unknown) => {
          if (alive.current) handleError(err, true);
        })
        .finally(() => {
          if (alive.current) setLoadingMore(false);
        });
    },
    [fetchPage, handleError],
  );

  const refresh = useCallback((): void => {
    if (pending.current > 0) return;
    fetchPage(null)
      .then((page) => {
        if (!alive.current) return;
        setState((prev) => {
          if (prev.items === null) return { items: page.items, cursor: page.nextCursor };
          const merged = mergeHead(prev.items, page.items, keyRef.current);
          return merged === prev.items ? prev : { items: merged, cursor: prev.cursor };
        });
        setError(false);
      })
      .catch((err: unknown) => {
        if (alive.current) handleError(err, false);
      });
  }, [fetchPage, handleError]);

  useEffect(() => {
    load(null);
  }, [load]);

  useLiveRefresh(refresh, REFRESH_INTERVAL_MS);

  const { items, cursor } = state;
  return {
    items,
    hasMore: cursor !== null,
    loadingMore,
    error,
    inert,
    loadMore: () => {
      if (cursor !== null) load(cursor);
    },
    retry: () => load(null),
  };
}
